import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TransformerViewModel } from '../../transformer-list/transformer-list.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonPasteDialogComponent } from '../../shared/json-paste-dialog/json-paste-dialog.component';
import { SmartSampleDialogComponent } from '../../shared/smart-sample-dialog/smart-sample-dialog.component';
import { AuditCompilerDialogComponent } from '../../shared/audit-compiler-dialog/audit-compiler-dialog.component';
import { ConfigService } from 'src/app/services/config/config.service';
import { UserDataService } from 'src/app/services/user-data-service';

/** Transform types with a compiled engine to audit against. Seeded with a
 * fallback guess for graceful behavior before the live answer arrives (or if
 * that call ever fails), then replaced with the authoritative list from
 * compiler_audit._ADAPTERS on the server the first time any instance of this
 * component constructs -- fetched once and shared by every instance, not
 * re-fetched per transformer. The Architect, 2026-09-30: this used to be a
 * hardcoded set "kept in sync by hand" with two OTHER backend lists that
 * don't actually mean the same thing (compile/verify support vs. audit-
 * adapter support) -- exactly how Row Extractor's button showed up before
 * its backend audit adapter existed. Mutable (not const) so the live refresh
 * below can replace its contents. */
let _auditableTransformTypes = new Set<number>([0, 7]);
let _auditableTypesFetchStarted = false;

export interface ITopHeaderDelegate {
  onTransformTextChanged(): void;
  viewTransformConfigJson(): void;
  runTransform(): void;
  saveConfig(): void;
  resetTransform(): void;
}

@Component({
    selector: 'app-transformer-top-header',
    templateUrl: './transformer-top-header.component.html',
    styleUrls: ['./transformer-top-header.component.css'],
    standalone: false
})
export class TransformerTopHeaderComponent {

  @Input() viewModel: any;
  @Input() public iconpath = "";
  @Input() public nodeColor = "";
  @Input() public transformNameType = "";
  @Input() public aboutlink = "";
  @Input() public refineryConfigGuid = "";
  @Input() public refineryName = "";
  @Input() public nodeName = "";
  @Input() public nodeIndex = "";
  @Input() public configGuid = "";
  @Input() transformslists: any;
  @Input() public isTransformName:boolean = true;
  @Input() public cacheStatus = "";
  /** Compiled operations plan hash, or null/empty when not yet compiled. */
  @Input() public compiledConfigHash: string | null = null;
  /** UTC timestamp of last successful compile. */
  @Input() public compiledAt: Date | null = null;
  /** Full compiled plan — used to check fullySupported and unsupportedRules. */
  @Input() public compiledConfig: any = null;
  /** The compiledConfigHash value "Audit Compiler Quality" was actually run against;
   *  null/empty = never audited (or an edit since then has moved compiledConfigHash
   *  past it, back to null via isCompiledStale). */
  @Input() public lastAuditHash: string | null = null;
  /** "match" or "mismatch" -- the outcome of the audit run against lastAuditHash. */
  @Input() public lastAuditResult: string | null = null;
  /** True when the transformer has at least one rule — drives the grey/orange/green indicator. */
  @Input() public hasRules: boolean = false;
  /** True when this transform type's detail component implements Reset Transformer.
   *  Defaults false so the button is disabled (not silently a no-op) until each
   *  transform type opts in with its own resetTransformer() implementation. */
  @Input() public hasResetSupport: boolean = false;
  /** Set by the parent transform-detail component from its own read-only
   * state (base-transformer-ui's isReadOnly getter, driven by
   * RefineryPresenceService.mode). Disables Save, Run, Reset, and every
   * paste/import entry point — the backend independently rejects the
   * save/run calls a Read-Only tab isn't holding the Edit lock for, but the
   * buttons stay disabled here too so a Read-Only Analyst never gets as
   * far as a rejected request. */
  @Input() public isReadOnly: boolean = false;
  @Output() onTransformTextChanged = new EventEmitter();
  @Output() onViewTransformConfigJson = new EventEmitter();
  @Output() onRunTransform = new EventEmitter();
  @Output() onSaveConfig = new EventEmitter();
  @Output() onResetTransform = new EventEmitter();
  @Output() onShowTransformDetail = new EventEmitter<string>();
  @Output() onImportTransformer = new EventEmitter<any>();
  //@Input() public delegate?: ITopHeaderDelegate = null;
  @Output() onRefreshCacheStatus = new EventEmitter();
  @Output() onPurgeCache = new EventEmitter();

  /** Smart Sample inputs — parent binds when available; button disabled when inputCacheKey is empty. */
  @Input() public inputCacheKey:        string  = '';
  @Input() public sourceColumn:         string  = '';
  /** Distinct fields referenced by the config's rules — populates the dialog's Source Field dropdown. */
  @Input() public availableSourceFields: string[] = [];
  @Input() public currentSampleStart:   number  = 0;
  @Input() public currentSampleEnd:     number  = 0;
  @Input() public smartSampleMode:      number  = 0;
  /** True when the current sample range matches the last Copilot-set Smart Sample range. */
  @Input() public smartSampleIsCurrent: boolean = false;
  @Output() onApplySmartSample = new EventEmitter<{ sampleStart: number; sampleEnd: number; sortBy?: string }>();

  /** Fires once an Audit Compiler Quality run completes with a persisted result, so the
   *  parent can update lastAuditHash/lastAuditResult immediately (they're @Input()s here,
   *  owned by base-transformer-ui) without waiting on an unrelated refresh/navigation to
   *  re-fetch them. */
  @Output() onAuditCompleted = new EventEmitter<{ lastAuditHash: string; lastAuditResult: string }>();

  /** "Audit Compiler Quality" -- transformType gates icon visibility (only
   * types with a compiled engine); currentConfigJson is the parent's own
   * live config (its own toTransformConfig()-equivalent), sent as-is to the
   * audit endpoint, which forces runAll=True/runSample=False server-side
   * regardless of what's in it. */
  @Input() public transformType: number = -1;
  @Input() public currentConfigJson: any = null;

  get isAuditable(): boolean {
    return _auditableTransformTypes.has(this.transformType);
  }

  /** Drives the audit button's color/enablement. Five states (the Architect,
   * 2026-10-01): grey/disabled when there is nothing to audit at all (no rules, or
   * this type has no compiled engine); orange/"unsaved" when this type supports
   * compiling but hasn't been saved yet (no compiledConfigHash to audit against);
   * blue once saved/compiled but not yet audited against THIS exact config (a save
   * with rule changes moves compiledConfigHash away from lastAuditHash, correctly
   * going blue again even if it was green/red before); green once audited and a
   * perfect match; red once audited and a mismatch was found. */
  get auditButtonState(): 'disabled' | 'unsaved' | 'blue' | 'green' | 'red' {
    if (!this.isAuditable || !this.hasRules) { return 'disabled'; }
    if (!this.compiledConfigHash) { return 'unsaved'; }
    if (!this.lastAuditHash || this.lastAuditHash !== this.compiledConfigHash) { return 'blue'; }
    return this.lastAuditResult === 'match' ? 'green' : 'red';
  }

  get auditButtonTooltip(): string {
    switch (this.auditButtonState) {
      case 'green':   return 'Audit Compiler Quality — last audit: perfect match';
      case 'red':     return 'Audit Compiler Quality — last audit found a mismatch';
      case 'blue':    return 'Audit Compiler Quality — not yet audited since the last change/save';
      case 'unsaved': return 'Please Save Transformer to create the Compiled version';
      default:        return this.isAuditable
        ? 'Add rules to this transformer before auditing'
        : 'This transform type has no compiled engine to audit';
    }
  }

  onAuditButtonClick(): void {
    const state = this.auditButtonState;
    if (state === 'blue' || state === 'green' || state === 'red') {
      this.openAuditCompiler();
    } else if (state === 'unsaved') {
      this.snackBar.open('Please Save Transformer to create the Compiled version', 'Dismiss', {
        duration: 4000
      });
    }
  }

  openAuditCompiler(): void {
    const dialogRef = this.dialog.open(AuditCompilerDialogComponent, {
      width: '560px',
      data: {
        transformType: this.transformType,
        config: this.currentConfigJson,
        inputCacheKey: this.inputCacheKey,
        // Identify which transform entry to write the audit result back onto
        // (lastAuditHash/lastAuditResult), driving the blue/green/red state above.
        // configGuid (this.configGuid, really the NODE's own config file guid --
        // same value already used for Copilot's own transformId elsewhere) names
        // transformerconfigPath/{configGuid}.json, which holds this node's whole
        // `transforms` array. transformGuid is THIS specific transform's own entry
        // guid within that array -- required because a node can chain several
        // transforms (T1, T2, ...) in one file. Root-caused 2026-10-01: the first
        // version of this sent refineryId/configGuid and looked inside the REFINERY
        // config's `nodes` list (which has no `transforms` key at all) for a `guid`
        // match against the node's configGuid -- never matched anything, so nothing
        // was ever persisted and the icon stayed blue after every audit.
        configGuid: this.configGuid,
        transformGuid: this.viewModel?.transform?.guid ?? '',
        // Only read by Column Lookup's own backend audit adapter, to resolve its second,
        // separate lookup/reference farm (a cache lookup keyed by these + the config's
        // own lookupFarm node id, mirroring the live engine exactly). Harmless no-ops for
        // every other transform type's adapter.
        orgId: this.userData.getOrgId(),
        userId: this.userData.getUserId(),
        refineryId: this.refineryConfigGuid,
      },
    });

    // The backend already persisted lastAuditHash/lastAuditResult (best-effort) by the
    // time the dialog shows a result -- reflect it immediately here instead of leaving
    // the icon stuck on its pre-audit state until some unrelated refresh/navigation
    // re-fetches these @Input()s. lastAuditHash mirrors compiledConfigHash exactly,
    // since the backend hashed the identical config this audit was run against.
    dialogRef.afterClosed().subscribe((report: { overallMatch: boolean } | undefined) => {
      if (report?.overallMatch === undefined || !this.compiledConfigHash) { return; }
      this.onAuditCompleted.emit({
        lastAuditHash: this.compiledConfigHash,
        lastAuditResult: report.overallMatch ? 'match' : 'mismatch',
      });
    });
  }

  constructor(private dialog: MatDialog, private snackBar: MatSnackBar, private configService: ConfigService, private userData: UserDataService) {
    if (!_auditableTypesFetchStarted) {
      _auditableTypesFetchStarted = true;
      this.configService.getAuditableTypes()
        .then(resp => { _auditableTransformTypes = new Set(resp.types); })
        .catch(() => { /* best-effort refresh -- keep the fallback guess on failure, never block the button on this */ });
    }
  }

  ngOnInit(): void {
    this.scrollToTransform(this.viewModel.transform.index);
  }
  TransformTextChanged(): void {
    this.onTransformTextChanged.emit();
  }
  viewTransformConfigJson(): void {
    this.onViewTransformConfigJson.emit();
    //this.delegate?.viewTransformConfigJson();
  }
  runTransform(): void {
    this.onRunTransform.emit();
  }
  saveConfig(): void {
    this.onSaveConfig.emit();
  }
  resetTransform(): void {
    this.onResetTransform.emit();
  }
  showTransformDetail(transformId: string): void {
    this.onShowTransformDetail.emit(transformId);
  }
  scrollToTransform(index: number): void {
    // Wait for the DOM to update before scrolling
    setTimeout(() => {
      const element = document.querySelector(`[data-index="${index}"]`);
      if (element) {
        (element as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          inline: 'center', // or 'nearest' or 'start'
          block: 'nearest'
        });
      }
    }, 0);
  }
  async importTransformer(): Promise<void>{
    const dialogRef = this.dialog.open(JsonPasteDialogComponent, {
      width: '600px',
      height: '420px',
      disableClose: true,
       data: { inputType: 'json' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onImportTransformer.emit(result);
      }
    });
  }
  
  async importFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    const text = await file.text();       // modern & clean
    const parsed = JSON.parse(text);      // validate JSON

    this.onImportTransformer.emit(parsed);

    // reset input so same file can be selected again
    input.value = '';
  }

  async refreshCacheStatus(): Promise<void>{
    this.onRefreshCacheStatus.emit();
  }

  async purgeCache(): Promise<void>{
    if (this.cacheStatus === 'Not Cached' || !this.cacheStatus) { return; }
    this.onPurgeCache.emit();
  }

  async openSmartSample(): Promise<void> {
    const dialogRef = this.dialog.open(SmartSampleDialogComponent, {
      width: '500px',
      disableClose: false,
      data: {
        inputCacheKey:         this.inputCacheKey,
        sourceColumn:          this.sourceColumn,
        availableSourceFields: this.availableSourceFields,
        currentSampleStart:    this.currentSampleStart,
        currentSampleEnd:      this.currentSampleEnd,
        smartSampleMode:       this.smartSampleMode,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      (document.activeElement as HTMLElement)?.blur();
      if (result) {
        this.onApplySmartSample.emit(result);
      }
    });
  }
}
